import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import queryString from 'query-string';
import { Formik } from 'formik';
import * as Yup from 'yup';

// Material components
import { CssBaseline, Snackbar } from '@material-ui/core';

// Icons
import Icon from '@mdi/react';
import { mdiAccount, mdiKey, mdiShieldKeyOutline, mdiSend, mdiAlertCircle } from '@mdi/js';
import { siteName, colors, din, globalStyle } from '../styles/variables';
import { commonPasswords } from '../util/util';
import OnPremBar from '../components/elements/OnPremBar';
import { FormInputTextBox } from '../components/elements/TextBoxes';
import {
  FormPageContainer,
  FormPageBox,
  FormPageButton,
  LogoContainer,
  AlleviLogo,
  StyledForm,
  FormText,
  FormPageLinkContainer,
  FormPageLink,
  LinkDivider
} from '../components/elements/FormPageElements';
import { useNucActions, useNucState, useOrganizationActions, useUserActions } from '../state/redux/hooks';

//
// Login Page
//

// Login validation
const LoginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email required'),
  password: Yup.string().min(8, 'Password must be longer than 8 characters').required('Password required')
});

// On-prem initialization validation
const InitSchema = Yup.object().shape({
  initCode: Yup.string().min(4, 'Initialization code is too short').required('Initialization code required'),
  email: Yup.string().email('Invalid email').required('Email required'),
  firstName: Yup.string().required('First name required'),
  lastName: Yup.string().required('Last name required'),
  password: Yup.string()
    .min(8, 'Password must be longer than 8 characters')
    .required('Password required')
    .notOneOf(commonPasswords, 'Password is too common'),
  passwordConfirm: Yup.string()
    .oneOf([Yup.ref('password'), null], "Passwords don't match")
    .required('Password confirmation is required')
});

const StyledSnackbar = styled(Snackbar)`
  & .MuiPaper-root {
    font-family: ${din};
    color: ${colors.gray87};
    background-color: ${colors.deliMustardLight};
  }
`;

interface LoginProps {
  location: {
    search: string;
  };
}

const Login = (props: LoginProps) => {
  const nuc = useNucState();
  const userActions = useUserActions();
  const nucActions = useNucActions();
  const organizationActions = useOrganizationActions();
  const history = useHistory();

  const onPrem = window.hostEnvironment && window.hostEnvironment === 'ON_PREM';

  const [status, setStatus] = useState(0);

  useEffect(() => {
    document.title = `Login | ${siteName}`;

    if (onPrem) {
      nucActions.isNUCFirstRun();
    }
  }, []); /*eslint-disable-line react-hooks/exhaustive-deps */ //TODO: verify

  const qs = queryString.parse(props.location.search);

  // Different screen for first run on NUC
  if (onPrem && nuc.status === 'idle' && nuc.firstRun) {
    return (
      <>
        <CssBaseline />

        <OnPremBar header />

        <FormPageContainer container direction="column" justify="center" alignItems="center" noHeader onPrem={onPrem}>
          <FormPageBox elevation={0}>
            <LogoContainer>
              <AlleviLogo />
            </LogoContainer>

            <Formik
              initialValues={{
                initCode: '',
                email: '',
                firstName: '',
                lastName: '',
                password: ''
              }}
              validationSchema={InitSchema}
              onSubmit={(values, { setSubmitting }) => {
                nucActions
                  //@ts-expect-error //TODO: validateNUCInitializationToken needs serialNumber and token to be passed?
                  .validateNUCInitializationToken(values.initCode)
                  .then(validateResponse => {
                    if (!validateResponse.success) {
                      setSubmitting(false);
                      setStatus(2);
                      return;
                    }

                    userActions
                      .registerUser(values.firstName, values.lastName, values.email, values.password)
                      .then(e => {
                        if (e.success) {
                          userActions.authenticateUser(values.email, values.password).then(authResponse => {
                            if (authResponse.success) {
                              nucActions.activateNUCPlan(values.initCode).then(() => {
                                organizationActions.getOrganization();
                                setStatus(1);
                                if (qs.ref) {
                                  history.push(decodeURIComponent(qs.ref as string));
                                } else {
                                  history.push('/');
                                }
                              });
                            } else {
                              setStatus(2);
                            }
                          });
                        } else if (e.statusCode === 500) {
                          setStatus(3);
                        } else {
                          setStatus(2);
                        }
                      })
                      .then(() => setSubmitting(false))
                      .catch(e => {
                        setSubmitting(false);
                        setStatus(2);
                      });
                  })
                  .catch(() => {
                    setSubmitting(false);
                    setStatus(4);
                  });
              }}
            >
              {({ errors, isSubmitting, handleSubmit }) => (
                <StyledForm onSubmit={handleSubmit}>
                  {status === 2 && (
                    <FormText>
                      <Icon path={mdiAlertCircle} color={colors.alleviRed} />
                      Error creating user account. Double-check your initialization code and try again
                    </FormText>
                  )}

                  {status === 3 && (
                    <FormText>
                      <Icon path={mdiAlertCircle} color={colors.alleviRed} />
                      Unable to connect to On-Premises Gateway, try again later
                    </FormText>
                  )}

                  {status === 4 && (
                    <FormText>
                      <Icon path={mdiAlertCircle} color={colors.alleviRed} />
                      Incorrect initialization code. Double-check the code and try again
                    </FormText>
                  )}

                  {status === 0 && (
                    <>
                      <FormText>It looks like this a new Allevi On-Premises Gateway</FormText>
                      <FormText>
                        <p>
                          Initialize your device by first adding it to your registered devices in{' '}
                          <a href="https://bioprint.allevi3d.com">Allevi Bioprint Online</a>, then use the provided
                          initialization code to create an On-Premises account
                        </p>
                      </FormText>
                    </>
                  )}

                  <FormInputTextBox
                    label="Initialization Code"
                    name="initCode"
                    icon={<Icon path={mdiShieldKeyOutline} />}
                    marginBottom={16}
                    wide
                    plain
                    password
                  />

                  <FormInputTextBox
                    label="Email Address"
                    name="email"
                    icon={<Icon path={mdiAccount} />}
                    marginBottom={16}
                    wide
                    plain
                    email
                  />

                  <FormInputTextBox label="First Name" name="firstName" wide plain />

                  <FormInputTextBox label="Last Name" name="lastName" wide plain />

                  <FormInputTextBox
                    label="Password"
                    name="password"
                    icon={<Icon path={mdiKey} />}
                    marginBottom={16}
                    wide
                    plain
                    password
                  />

                  <FormInputTextBox
                    label="Confirm Password"
                    name="passwordConfirm"
                    icon={<Icon path={mdiKey} />}
                    marginBottom={16}
                    wide
                    plain
                    password
                  />

                  <FormPageButton
                    primary
                    type="submit"
                    disabled={Object.keys(errors).length > 0}
                    //@ts-expect-error
                    onSubmit={handleSubmit}
                  >
                    <Icon path={mdiSend} />
                    <p>Initialize Device</p>
                  </FormPageButton>
                </StyledForm>
              )}
            </Formik>
          </FormPageBox>
        </FormPageContainer>
      </>
    );
  }

  return (
    <>
      <CssBaseline />

      <OnPremBar header />

      <FormPageContainer container direction="column" justify="center" alignItems="center">
        <FormPageBox elevation={0}>
          <LogoContainer>
            <AlleviLogo />
          </LogoContainer>

          {qs.reason && qs.reason === 'timeout' && (
            <StyledSnackbar
              open
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              message={<span>You have been automatically logged out for security reasons – Please log in again</span>}
            />
          )}

          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={LoginSchema}
            onSubmit={(values, { setSubmitting }) => {
              userActions
                .authenticateUser(values.email, values.password)
                .then(e => {
                  if (e.success) {
                    setStatus(1);
                    if (qs.ref) {
                      // Handle external and internal redirect separately
                      if (new URL(decodeURIComponent(qs.ref as string)).hostname !== window.location.hostname) {
                        if (new URL(decodeURIComponent(qs.ref as string)).hostname.includes('allevi')) {
                          //@ts-expect-error //TODO: string can't be assigned to location
                          window.location = decodeURIComponent(qs.ref as string);
                        } else {
                          history.push('/');
                        }
                      } else {
                        history.push(decodeURIComponent(qs.ref as string));
                      }
                    } else {
                      history.push('/');
                    }
                  } else if (e.statusCode === 500) {
                    setStatus(3);
                  } else {
                    setStatus(2);
                  }
                })
                .then(() => setSubmitting(false))
                .catch(e => {
                  setSubmitting(false);
                  setStatus(2);
                });
            }}
          >
            {({ errors, isSubmitting, handleSubmit }) => (
              <StyledForm onSubmit={handleSubmit}>
                {status === 2 && (
                  <FormText>
                    <Icon path={mdiAlertCircle} color={colors.alleviRed} />
                    Your username or password is incorrect
                  </FormText>
                )}

                {status === 3 && (
                  <FormText>
                    <Icon path={mdiAlertCircle} color={colors.alleviRed} />
                    Unable to connect to Allevi server, try again later
                  </FormText>
                )}

                <FormInputTextBox
                  label="Email Address"
                  name="email"
                  icon={<Icon path={mdiAccount} />}
                  marginBottom={16}
                  wide
                  plain
                  email
                />

                <FormInputTextBox
                  label="Password"
                  name="password"
                  icon={<Icon path={mdiKey} />}
                  marginBottom={16}
                  wide
                  plain
                  password
                />
                {/*@ts-expect-error */}
                <FormPageButton primary type="submit" disabled={Object.keys(errors).length > 0} onSubmit={handleSubmit}>
                  <Icon path={mdiSend} />
                  <p>Login</p>
                </FormPageButton>
              </StyledForm>
            )}
          </Formik>

          <FormPageLinkContainer>
            <FormPageLink href="/forgot-password">Forgot your password?</FormPageLink>
            <LinkDivider />
            <FormPageLink href="/create-account">Create an account</FormPageLink>
          </FormPageLinkContainer>
        </FormPageBox>
      </FormPageContainer>
    </>
  );
};

export default withStyles(globalStyle)(Login);
